import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { liteClient } from 'algoliasearch/lite';
import { listingsAPI, locationsAPI, Listing } from '../api';
import ListingCard from '../components/ListingCard';
import { loadHomeDeliveryLocation } from '../utils/homeLocationStorage';
import { getListingDistances } from '../utils/listingDistanceFilter';
import { algoliaHitToListing, searchAlgoliaListings } from '../services/algoliaSearch';
import { formatListingCategoryLabel, normalizeListingCategory } from '../constants/listingCategories';

interface TopCategory {
  category: string;
  count: number;
}

const searchClient = liteClient('NC39OBLGG3', 'dc77e2a6d622659fd216427832b00f1d');

const PAGE_SIZE = 50;

function countActiveSearchFilters(params: {
  zipCode: string;
  miles: string;
  minPrice: string;
  maxPrice: string;
  condition: string;
  sellerType: string;
}): number {
  return [
    params.zipCode,
    params.miles !== '25' ? params.miles : '',
    params.minPrice,
    params.maxPrice,
    params.condition,
    params.sellerType,
  ].filter(Boolean).length;
}

interface SearchFiltersFieldsProps {
  filterZipCode: string;
  setFilterZipCode: (value: string) => void;
  filterMiles: string;
  setFilterMiles: (value: string) => void;
  filterMinPrice: string;
  setFilterMinPrice: (value: string) => void;
  filterMaxPrice: string;
  setFilterMaxPrice: (value: string) => void;
  filterCondition: string;
  setFilterCondition: (value: string) => void;
  filterSellerType: string;
  setFilterSellerType: (value: string) => void;
  zipLoading: boolean;
  locationLabel: string;
  zipLocation: { lat: number; lng: number } | null;
}

const SearchFiltersFields: React.FC<SearchFiltersFieldsProps> = ({
  filterZipCode,
  setFilterZipCode,
  filterMiles,
  setFilterMiles,
  filterMinPrice,
  setFilterMinPrice,
  filterMaxPrice,
  setFilterMaxPrice,
  filterCondition,
  setFilterCondition,
  filterSellerType,
  setFilterSellerType,
  zipLoading,
  locationLabel,
  zipLocation,
}) => (
  <>
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">Location &amp; available deliveries</h3>
      <p className="text-xs text-gray-500 mb-3">
        Location is where you receive deliveries. Available deliveries limits results to sellers who can deliver within that distance.
      </p>
      <label className="block text-sm font-medium text-gray-700 mb-2">Location (ZIP)</label>
      <div className="relative mb-4">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <i className="ri-map-pin-line" aria-hidden />
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={filterZipCode}
          onChange={(e) => setFilterZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          placeholder="00000"
          maxLength={5}
          className="w-full max-w-full min-w-0 box-border pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
        />
      </div>
      {zipLoading && (
        <p className="text-xs text-gray-500 mb-3">Checking delivery area…</p>
      )}
      {!zipLoading && !filterZipCode && locationLabel && zipLocation && (
        <p className="text-xs text-gray-600 mb-3">
          Using delivery location from home: {locationLabel}
        </p>
      )}
      <label className="block text-sm font-medium text-gray-700 mb-2">Available deliveries within (miles)</label>
      <select
        value={filterMiles}
        onChange={(e) => setFilterMiles(e.target.value)}
        className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
      >
        <option value="5">5 miles</option>
        <option value="10">10 miles</option>
        <option value="25">25 miles</option>
        <option value="50">50 miles</option>
        <option value="100">100 miles</option>
        <option value="250">250 miles</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Price range</label>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          value={filterMinPrice}
          onChange={(e) => setFilterMinPrice(e.target.value)}
          placeholder="Min"
          min="0"
          className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
        />
        <input
          type="number"
          value={filterMaxPrice}
          onChange={(e) => setFilterMaxPrice(e.target.value)}
          placeholder="Max"
          min="0"
          className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
      <select
        value={filterCondition}
        onChange={(e) => setFilterCondition(e.target.value)}
        className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
      >
        <option value="">All</option>
        <option value="new">New</option>
        <option value="new_open_box">New – Open box</option>
        <option value="seller_refurbished">Seller refurbished</option>
        <option value="used">Used</option>
        <option value="for_parts">For parts or not working</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Seller type</label>
      <select
        value={filterSellerType}
        onChange={(e) => setFilterSellerType(e.target.value)}
        className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
      >
        <option value="">All</option>
        <option value="store">Store</option>
        <option value="owner">Owner</option>
      </select>
    </div>
  </>
);

function initialZipFromParams(searchParams: URLSearchParams): string {
  const fromUrl = searchParams.get('zip') || '';
  if (fromUrl) return fromUrl;
  return loadHomeDeliveryLocation()?.zip || '';
}

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [zipCode, setZipCode] = useState(() => initialZipFromParams(searchParams));
  const [miles, setMiles] = useState(searchParams.get('miles') || '25');
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [condition, setCondition] = useState(searchParams.get('condition') || '');
  const [sellerType, setSellerType] = useState(searchParams.get('sellerType') || '');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [zipLocation, setZipLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [zipLoading, setZipLoading] = useState(false);
  const [topCategories, setTopCategories] = useState<TopCategory[]>([]);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedCategory, setSelectedCategory] = useState(
    () => searchParams.get('category') || ''
  );
  
  // Filter state (separate from applied filters)
  const [filterZipCode, setFilterZipCode] = useState(() => initialZipFromParams(searchParams));
  const [filterMiles, setFilterMiles] = useState(searchParams.get('miles') || '25');
  const [filterMinPrice, setFilterMinPrice] = useState(searchParams.get('minPrice') || '');
  const [filterMaxPrice, setFilterMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [filterCondition, setFilterCondition] = useState(searchParams.get('condition') || '');
  const [filterSellerType, setFilterSellerType] = useState(searchParams.get('sellerType') || '');
  const [locationLabel, setLocationLabel] = useState(() => loadHomeDeliveryLocation()?.label || '');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [listingDistances, setListingDistances] = useState<Record<number, number>>({});

  useEffect(() => {
    const loadTopCategories = async () => {
      try {
        const categories = await listingsAPI.getTopCategories(0);
        setTopCategories(Array.isArray(categories) ? categories : []);
      } catch (error) {
        console.error('Error loading top categories:', error);
        setTopCategories([]);
      }
    };
    loadTopCategories();
  }, []);

  // Get search suggestions as user types
  useEffect(() => {
    const getSuggestions = async () => {
      if (!query || query.length < 2) {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        // Use the same search structure as the main search
        const response = await searchClient.search({
          requests: [
            {
              indexName: 'listings',
              query: query,
              params: {
                hitsPerPage: 5,
              } as any, // Type assertion to bypass TypeScript error
            },
          ],
        });

        const firstResult = response.results?.[0] as any;
        const hits = firstResult?.hits || [];
        
        // Extract unique suggestions from hits (titles and categories)
        const suggestions = new Set<string>();
        hits.forEach((hit: any) => {
          if (hit.title) {
            suggestions.add(hit.title);
          }
          if (hit.category) {
            suggestions.add(hit.category);
          }
        });

        const suggestionsArray = Array.from(suggestions).slice(0, 8);
        
        setSearchSuggestions(suggestionsArray);
        setShowSuggestions(suggestionsArray.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const timeoutId = setTimeout(() => {
      getSuggestions();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Apply filters function
  const applyFilters = () => {
    // Update applied filters from filter state
    setZipCode(filterZipCode);
    setMiles(filterMiles);
    setMinPrice(filterMinPrice);
    setMaxPrice(filterMaxPrice);
    setCondition(filterCondition);
    setSellerType(filterSellerType);
    
    // Update URL params immediately
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedCategory) params.set('category', selectedCategory);
    if (filterZipCode) params.set('zip', filterZipCode);
    if (filterMiles) params.set('miles', filterMiles);
    if (filterMinPrice) params.set('minPrice', filterMinPrice);
    if (filterMaxPrice) params.set('maxPrice', filterMaxPrice);
    if (filterCondition) params.set('condition', filterCondition);
    if (filterSellerType) params.set('sellerType', filterSellerType);
    setSearchParams(params, { replace: true });
  };

  // Clear filters function
  const clearFilters = () => {
    setFilterZipCode('');
    setFilterMiles('25');
    setFilterMinPrice('');
    setFilterMaxPrice('');
    setFilterCondition('');
    setFilterSellerType('');
    setZipCode('');
    setMiles('25');
    setMinPrice('');
    setMaxPrice('');
    setCondition('');
    setSellerType('');
    setQuery('');
    setSelectedCategory('');
  };

  // Get lat/lng from zip code or home page delivery location
  useEffect(() => {
    const fetchZipLocation = async () => {
      if (zipCode && zipCode.length === 5) {
        setZipLoading(true);
        try {
          const data = await locationsAPI.lookupZipCode(zipCode);
          if (data && data.latitude && data.longitude) {
            setZipLocation({
              lat: parseFloat(data.latitude),
              lng: parseFloat(data.longitude),
            });
          } else {
            setZipLocation(null);
          }
        } catch (error) {
          console.error('Error fetching zip code location:', error);
          setZipLocation(null);
        } finally {
          setZipLoading(false);
        }
        return;
      }

      const stored = loadHomeDeliveryLocation();
      if (stored?.lat != null && stored?.lng != null) {
        setZipLocation({ lat: stored.lat, lng: stored.lng });
        if (stored.label) setLocationLabel(stored.label);
        return;
      }

      setZipLocation(null);
    };

    fetchZipLocation();
  }, [zipCode]);

  // Perform search
  const performSearch = async (append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const fetchOffset = append ? searchOffset : 0;
      const searchTerm = query.trim();
      let allListings: Listing[];

      if (searchTerm) {
        const page = Math.floor(fetchOffset / PAGE_SIZE);
        const categoryKey = selectedCategory
          ? normalizeListingCategory(selectedCategory)
          : '';
        const result = await searchAlgoliaListings({
          query: searchTerm,
          page,
          hitsPerPage: PAGE_SIZE,
          category: selectedCategory || undefined,
        });
        allListings = result.hits
          .map((hit) => ({ ...algoliaHitToListing(hit), sold: 0 }))
          .filter(
            (listing) =>
              !categoryKey ||
              normalizeListingCategory(listing.category || '') === categoryKey
          );
        setSearchOffset(fetchOffset + PAGE_SIZE);
        setHasMore(page + 1 < result.nbPages);
      } else {
        const filters: any = { limit: PAGE_SIZE, offset: fetchOffset };
        if (selectedCategory) filters.category = selectedCategory;
        allListings = await listingsAPI.getAll(filters);
        const rawCount = Array.isArray(allListings) ? allListings.length : 0;
        setSearchOffset(fetchOffset + rawCount);
        setHasMore(rawCount >= PAGE_SIZE);
      }

      // Filter listings client-side
        let filteredListings = allListings.filter(
          (listing: Listing) => listing.sold === 0 || listing.sold === false
        );

        // Apply condition filter
        if (condition) {
          filteredListings = filteredListings.filter(
            (listing: Listing) => {
              const listingCondition = listing.condition || 'new';
              return listingCondition === condition;
            }
          );
        }

        // Apply seller type filter
        if (sellerType === 'store') {
          filteredListings = filteredListings.filter(
            (listing: Listing) => listing.store_id || listing.store_name
          );
        } else if (sellerType === 'owner') {
          filteredListings = filteredListings.filter(
            (listing: Listing) => !listing.store_id && !listing.store_name
          );
        }

        // Apply price filters
        if (minPrice && minPrice.trim() !== '') {
          const minPriceNum = parseFloat(minPrice);
          if (!isNaN(minPriceNum)) {
            filteredListings = filteredListings.filter(
              (listing: Listing) => (listing.price || 0) >= minPriceNum
            );
          }
        }
        if (maxPrice && maxPrice.trim() !== '') {
          const maxPriceNum = parseFloat(maxPrice);
          if (!isNaN(maxPriceNum)) {
            filteredListings = filteredListings.filter(
              (listing: Listing) => (listing.price || 0) <= maxPriceNum
            );
          }
        }

        // Distance from delivery location + optional radius filter
        let pageDistances: Record<number, number> = {};
        if (zipLocation) {
          pageDistances = await getListingDistances(filteredListings, zipLocation);
          const milesNum = parseFloat(miles);
          if (!isNaN(milesNum) && milesNum > 0) {
            filteredListings = filteredListings.filter((listing: Listing) => {
              const distance = pageDistances[listing.id];
              return distance != null && distance <= milesNum;
            });
          }
          if (append) {
            setListingDistances((prev) => ({ ...prev, ...pageDistances }));
          } else {
            setListingDistances(pageDistances);
          }
        } else if (!append) {
          setListingDistances({});
        }

      if (append) {
        setResults(prev => [...prev, ...filteredListings]);
      } else {
        setResults(filteredListings);
      }
    } catch (error) {
      console.error('Search error:', error);
      if (!append) setResults([]);
      if (!append) setListingDistances({});
    } finally {
      setLoading(false);
      if (append) setLoadingMore(false);
    }
  };

  // Run search when filters change
  useEffect(() => {
    setSearchOffset(0);
    setHasMore(false);
    const hasFilters = query || selectedCategory || zipCode || minPrice || maxPrice || condition || sellerType;
    
    if (!hasFilters) {
      performSearch();
    } else {
      const timeoutId = setTimeout(() => performSearch(), 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedCategory, zipLocation, miles, minPrice, maxPrice, condition, sellerType, zipCode]);

  // Sync filter state with applied filters when URL params change (on mount or when URL changes externally)
  useEffect(() => {
    const urlZip = searchParams.get('zip') || '';
    const urlMiles = searchParams.get('miles') || '25';
    const urlMinPrice = searchParams.get('minPrice') || '';
    const urlMaxPrice = searchParams.get('maxPrice') || '';
    const urlCondition = searchParams.get('condition') || '';
    const urlSellerType = searchParams.get('sellerType') || '';
    
    // Sync filter state with URL params (only if different to avoid unnecessary updates)
    if (urlZip !== filterZipCode) setFilterZipCode(urlZip);
    if (urlMiles !== filterMiles) setFilterMiles(urlMiles);
    if (urlMinPrice !== filterMinPrice) setFilterMinPrice(urlMinPrice);
    if (urlMaxPrice !== filterMaxPrice) setFilterMaxPrice(urlMaxPrice);
    if (urlCondition !== filterCondition) setFilterCondition(urlCondition);
    if (urlSellerType !== filterSellerType) setFilterSellerType(urlSellerType);
    
    // Sync applied filters with URL params (only if different to avoid unnecessary updates)
    if (urlZip !== zipCode) setZipCode(urlZip);
    if (urlMiles !== miles) setMiles(urlMiles);
    if (urlMinPrice !== minPrice) setMinPrice(urlMinPrice);
    if (urlMaxPrice !== maxPrice) setMaxPrice(urlMaxPrice);
    if (urlCondition !== condition) setCondition(urlCondition);
    if (urlSellerType !== sellerType) setSellerType(urlSellerType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedCategory) params.set('category', selectedCategory);
    if (zipCode) params.set('zip', zipCode);
    if (miles) params.set('miles', miles);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (condition) params.set('condition', condition);
    if (sellerType) params.set('sellerType', sellerType);
    setSearchParams(params, { replace: true });
  }, [query, selectedCategory, zipCode, miles, minPrice, maxPrice, condition, sellerType, setSearchParams]);

  const activeFilterCount = countActiveSearchFilters({
    zipCode,
    miles,
    minPrice,
    maxPrice,
    condition,
    sellerType,
  });

  const handleApplyFilters = () => {
    applyFilters();
    setMobileFiltersOpen(false);
  };

  const handleClearFilters = () => {
    clearFilters();
    setMobileFiltersOpen(false);
  };

  const filterFieldProps = {
    filterZipCode,
    setFilterZipCode,
    filterMiles,
    setFilterMiles,
    filterMinPrice,
    setFilterMinPrice,
    filterMaxPrice,
    setFilterMaxPrice,
    filterCondition,
    setFilterCondition,
    filterSellerType,
    setFilterSellerType,
    zipLoading,
    locationLabel,
    zipLocation,
  };

  const hasAnyFilters =
    query ||
    zipCode ||
    minPrice ||
    maxPrice ||
    condition ||
    sellerType ||
    filterZipCode ||
    filterMinPrice ||
    filterMaxPrice ||
    filterCondition ||
    filterSellerType;

  /** Prefer category pill total when search isn't narrowed further (Found was only the loaded page size). */
  const foundCount = (() => {
    const milesNum = parseFloat(miles);
    const distanceActive = !!zipLocation && Number.isFinite(milesNum) && milesNum > 0;
    const narrowed =
      distanceActive ||
      !!(query || minPrice || maxPrice || condition || sellerType);
    if (selectedCategory && !narrowed) {
      const key = normalizeListingCategory(selectedCategory);
      const cat = topCategories.find(
        (c) => normalizeListingCategory(String(c.category)) === key
      );
      const total = cat != null ? Number(cat.count) : NaN;
      if (Number.isFinite(total) && total >= results.length) return total;
    }
    return results.length;
  })();

  const dedupedTopCategories = useMemo(() => {
    const seen = new Set<string>();
    return topCategories.filter((cat) => {
      const key = normalizeListingCategory(String(cat.category));
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [topCategories]);

  const categoryIsSelected = (category: string) => {
    if (!selectedCategory.trim()) return false;
    return (
      normalizeListingCategory(selectedCategory) === normalizeListingCategory(category)
    );
  };

  const toggleCategory = (category: string) => {
    setSelectedCategory(categoryIsSelected(category) ? '' : category);
  };

  const searchPlaceholder = selectedCategory.trim()
    ? `Search in ${formatListingCategoryLabel(selectedCategory)}`
    : 'Search for anything';

  const categoryPills = dedupedTopCategories.length > 0 && (
    <div className="border-t border-gray-100 pt-3 mt-3 sm:pt-4 sm:mt-4">
      <p className="text-xs font-medium text-gray-500 mb-2 sm:mb-3">Categories</p>
      <div className="-mx-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max min-w-full flex-nowrap gap-2 px-1 pb-0.5">
        <button
          type="button"
          onClick={() => setSelectedCategory('')}
          className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
            !selectedCategory
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {dedupedTopCategories.map((cat) => (
          <button
            key={normalizeListingCategory(String(cat.category))}
            type="button"
            onClick={() => toggleCategory(String(cat.category))}
            className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              categoryIsSelected(String(cat.category))
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {formatListingCategoryLabel(String(cat.category))}
            <span className="ml-1 tabular-nums opacity-80">{Number(cat.count) || 0}</span>
          </button>
        ))}
        </div>
      </div>
    </div>
  );

  const suggestionList = showSuggestions && searchSuggestions.length > 0 && (
    <div
      className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
      style={{ top: '100%' }}
    >
      {searchSuggestions.map((suggestion, index) => (
        <button
          key={index}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            setQuery(suggestion);
            setShowSuggestions(false);
          }}
          className="w-full text-left px-4 py-2.5 text-base text-gray-800 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
        >
          {suggestion}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <div className="mx-auto w-full max-w-7xl min-w-0 px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">Search</h1>

        {/* Search Bar — desktop */}
        <div className="hidden lg:block rounded-xl border border-gray-200 bg-white p-4 sm:p-5 mb-4 sm:mb-6">
          <div className="relative min-w-0">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <i className="ri-search-line text-lg" aria-hidden />
            </div>
            <input
              ref={searchInputRef}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onFocus={() => {
                if (query.length >= 2 && searchSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder={searchPlaceholder}
              className="w-full min-w-0 box-border rounded-lg border border-gray-300 bg-white pl-11 pr-4 py-2.5 text-base focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
            {suggestionList}
          </div>
          {categoryPills}
        </div>

        {/* Search + filters — mobile */}
        <div className="lg:hidden rounded-xl border border-gray-200 bg-white p-4 mb-4">
          <div className="flex items-center gap-2 min-w-0 max-w-full">
            <div className="relative flex-1 basis-0 min-w-0">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <i className="ri-search-line text-lg" aria-hidden />
              </div>
              <input
                ref={searchInputRef}
                type="search"
                enterKeyHint="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => {
                  if (query.length >= 2 && searchSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder={searchPlaceholder}
                className="w-full max-w-full min-w-0 box-border rounded-lg border border-gray-300 bg-white pl-10 pr-4 py-2.5 text-base focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
              />
              {suggestionList}
            </div>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((open) => !open)}
              aria-expanded={mobileFiltersOpen}
              aria-label="Filters"
              className={`relative shrink-0 flex items-center justify-center w-11 h-11 rounded-full border transition-colors ${
                mobileFiltersOpen || activeFilterCount > 0
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className="ri-equalizer-line text-xl" aria-hidden />
              {activeFilterCount > 0 && (
                <span className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-green-700 bg-white border border-green-600 rounded-full pointer-events-none">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {categoryPills}

          {mobileFiltersOpen && (
            <div className="flex flex-col gap-5 mt-4 pt-4 border-t border-gray-200">
              <SearchFiltersFields {...filterFieldProps} />
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="w-full rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                >
                  Apply filters
                </button>
                {hasAnyFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 min-w-0">
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-4 rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="text-base font-semibold text-gray-900">Filters</h2>
              <p className="mt-1 mb-4 text-xs text-gray-500">
                Location and delivery radius limit results to sellers who can deliver to you.
              </p>

              <div className="space-y-5 mb-5">
                <SearchFiltersFields {...filterFieldProps} />
              </div>

              <div className="space-y-2">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="w-full rounded-lg bg-green-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                >
                  Apply filters
                </button>
                {hasAnyFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          <div className="lg:col-span-3 min-w-0">

            {/* Results */}
            {loading ? (
              <div className="py-16 text-center">
                <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                <p className="mt-4 text-sm text-gray-600">Searching</p>
              </div>
            ) : results.length > 0 ? (
              <>
                <p className="mb-4 text-sm text-gray-600">
                  Found{' '}
                  <span className="font-semibold text-gray-900">
                    {foundCount}
                    {hasMore && foundCount === results.length ? '+' : ''}
                  </span>{' '}
                  result{foundCount !== 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 min-w-0">
                  {results.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      showBadges={false}
                      showOpenStatus={false}
                      showDistanceFooter
                      distanceMiles={listingDistances[listing.id]}
                    />
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-8 flex justify-center">
                    <button
                      type="button"
                      onClick={() => performSearch(true)}
                      disabled={loadingMore}
                      className="inline-flex h-12 min-w-[9.5rem] items-center justify-center gap-2 rounded-lg bg-green-600 px-6 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-70 transition-colors"
                    >
                      {loadingMore ? (
                        <>
                          <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" aria-hidden />
                          Loading
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-xl border border-gray-200 bg-white py-16 text-center px-4">
                <i className="ri-inbox-line text-5xl text-gray-300 mb-3" aria-hidden />
                <p className="text-base font-semibold text-gray-900">No listings found</p>
                <p className="mt-1 text-sm text-gray-600">Try a different search or adjust filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;

